<script setup>
import { computed } from 'vue'
import { homePath } from '@/utils/site-handle'

// Define props
const props = defineProps({
  navigation: {
    type: Array,
    default: () => []
  },
  additionalLanguages: {
    type: Array,
    default: () => []
  },
  siteHandle: {
    type: [String],
    default: ''
  },
  visibilityOptions: {
    type: String,
    default: ''
  },
  customHeaderCta: {
    type: Object,
    default: () => ({})
  },
  globalCtaSettings: {
    type: Object,
    default: () => ({})
  }
})

const homeLink = computed(() => homePath(props.siteHandle))

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
  
  <!-- Desktop Header -->
  <LayoutHeaderDesktop
    :navigation="navigation"
    :additional-languages="additionalLanguages"
    :siteHandle="siteHandle"
    :home-link="homeLink"
    :visibility-options="visibilityOptions"
    :custom-header-cta="customHeaderCta"
    :global-cta-settings="globalCtaSettings"
    class="layout-header-desk"
  />

  <!-- Mobile Header -->
  <LayoutHeaderMobile
    :navigation="navigation"
    :additional-languages="additionalLanguages"
    :siteHandle="siteHandle"
    :home-link="homeLink"
    :visibility-options="visibilityOptions"
    :custom-header-cta="customHeaderCta"
    :global-cta-settings="globalCtaSettings"
    class="layout-header-mobile"
  />

</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

/* Layout utilities */
.layout-header-desk {
  display: none;
}

@media (min-width: 1200px) {
  .layout-header-desk {
    display: block;
    position: sticky;
    top: 0;
    z-index: 1000;
  }
  
  .layout-header-mobile {
    display: none;
  }
}

</style>

<style>


header {
  transition: all 0.3s ease;
}

header .toggle__label {
  color: var(--color-gray-900);
  /* Force hardware acceleration on iOS */
  will-change: color;
  transform: translateZ(0);
  transition: color 0.3s ease;
  /* Force repaint on iOS */ 
  -webkit-font-smoothing: antialiased;
  backface-visibility: hidden;
}

header .header-inner {
  width: 100%;
  background: var(--color-white);
  transition: background 0.3s ease, border-bottom 0.3s ease;
}

.page.--overlapping-header .layout-header-desk:not(.is-scrolled) {
  pointer-events: none;

  .left,
  .center nav .top-nav-item,
  .right a,
  .right .header-cta-button,
  .right .locale-selector {
    pointer-events: auto;
  }
}

.page.--overlapping-header .layout-header-mobile:not(.is-scrolled) {
  pointer-events: none;

  .logo,
  .right,
  .site-links,
  .mobile-navigation {
    pointer-events: auto;
  }
}
  
.page.--overlapping-header header:not(.is-scrolled) .header-inner {
  position: absolute;
  width: 100%;
  left: 50%;
  transform: translateX(-50%);
}

.page.--overlapping-header .layout-header-desk:not(.is-scrolled) .header-inner:not(:hover) {
  background: transparent;
}

.page.--overlapping-header .layout-header-mobile:not(.is-scrolled) .header-inner:not(:has(.header-mobile .logo.is-open)) {
  background: transparent;
}

.page.--overlapping-header-dark .layout-header-mobile:not(.is-scrolled) .header-inner:not(:has(.header-mobile .logo.is-open)) {
  background: transparent;
}

.page.--overlapping-header-dark header:not(.is-scrolled):not(:hover) .columns,
.page.--overlapping-header-dark header:not(.is-scrolled):not(:hover) .transparent-button .btn-text,
.page.--overlapping-header-dark header:not(.is-scrolled):not(:hover):not(:has(.header-mobile .logo.is-open)) .toggle__label {
  color: var(--color-white);
  will-change: color;
  transform: translateZ(0);
  /* Force repaint on iOS */
  -webkit-font-smoothing: antialiased;
  backface-visibility: hidden;
  opacity: 1;
}

.page.--overlapping-header-dark header:not(.is-scrolled):not(:hover):not(:has(.header-mobile .logo.is-open)) .right .toggle path,
.page.--overlapping-header-dark header:not(.is-scrolled):not(:hover):not(:has(.header-mobile .logo.is-open)) .right .hamburger path {
  fill: var(--color-white);
  will-change: fill;
  transform: translateZ(0);
  transition: fill 0.3s ease;
  /* Force repaint on iOS */
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  opacity: 1;
}

.page.--overlapping-header-dark header:not(.is-scrolled):not(:hover) .logo svg path {
  fill: var(--color-white);
  will-change: fill;
  transform: translateZ(0);
  transition: fill 0.3s ease;
  /* Force repaint on iOS */
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  opacity: 1;
}

.page.--overlapping-header-dark header:not(.is-scrolled):not(:hover) .transparent-button g circle,
.page.--overlapping-header-dark header:not(.is-scrolled):not(:hover) .transparent-button g path {
  stroke: var(--color-white) !important;
}

.page.--overlapping-header-dark header:not(.is-scrolled):not(:hover) .btn-primary .btn-bg {
  background: var(--color-white);
}

.page.--overlapping-header-dark header:not(.is-scrolled):not(:hover) .btn-primary .btn-text {
  color: var(--color-black);
}

.page.--overlapping-header-dark header:not(.is-scrolled):not(:hover) .btn-primary .btn-icon path {
  fill: var(--color-white);
}

.page.--overlapping-header-dark header:not(.is-scrolled):not(:hover) .btn-primary .btn-icon rect {
  fill: var(--color-white);
}

/* Explicit styles for is-scrolled state to fix iOS Safari */
.page.--overlapping-header-dark header.is-scrolled .columns,
.page.--overlapping-header-dark header.is-scrolled .transparent-button .btn-text,
.page.--overlapping-header-dark header.is-scrolled .toggle__label {
  color: var(--color-gray-900) !important;
  /* Force repaint on iOS */
  will-change: color;
  transform: translateZ(0);
  -webkit-font-smoothing: antialiased;
  backface-visibility: hidden;
}

.page.--overlapping-header-dark header.is-scrolled .right .toggle path,
.page.--overlapping-header-dark header.is-scrolled .right .hamburger path {
  fill: var(--color-gray-900) !important;
  /* Force repaint on iOS */
  will-change: fill;
  transform: translateZ(0);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
}

.page.--overlapping-header-dark header.is-scrolled .transparent-button g circle,
.page.--overlapping-header-dark header.is-scrolled .transparent-button g path {
  stroke: var(--color-gray-900) !important;
}

/* Explicit styles for :hover state to fix iOS Safari */
.page.--overlapping-header-dark header:not(.is-scrolled):hover .columns,
.page.--overlapping-header-dark header:not(.is-scrolled):hover .transparent-button .btn-text,
.page.--overlapping-header-dark header:not(.is-scrolled):hover .right .locale-selector .toggle__label {
  color: var(--color-gray-900) !important;
}

.page.--overlapping-header-dark header:not(.is-scrolled):hover .right .toggle path,
.page.--overlapping-header-dark header:not(.is-scrolled):hover .right .hamburger path {
  fill: var(--color-gray-900) !important;
}

.page.--overlapping-header-dark header:not(.is-scrolled):hover .transparent-button g circle,
.page.--overlapping-header-dark header:not(.is-scrolled):hover .transparent-button g path {
  stroke: var(--color-gray-900) !important;
}

/* Explicit styles for when mobile menu is open (is-open) to fix iOS Safari */
.page.--overlapping-header-dark header:not(.is-scrolled) .header-mobile .logo.is-open ~ .right .toggle__label,
.page.--overlapping-header-dark header:not(.is-scrolled) .header-mobile:has(.logo.is-open) .right .toggle__label {
  color: var(--color-gray-900) !important;
  /* Force repaint on iOS */
  will-change: color;
  transform: translateZ(0);
}

.page.--overlapping-header-dark header:not(.is-scrolled) .header-mobile .logo.is-open ~ .right .toggle path,
.page.--overlapping-header-dark header:not(.is-scrolled) .header-mobile .logo.is-open ~ .right .hamburger path,
.page.--overlapping-header-dark header:not(.is-scrolled) .header-mobile:has(.logo.is-open) .right .toggle path,
.page.--overlapping-header-dark header:not(.is-scrolled) .header-mobile:has(.logo.is-open) .right .hamburger path {
  fill: var(--color-gray-900) !important;
  /* Force repaint on iOS */
  will-change: fill;
  transform: translateZ(0);
}

/* Additional iOS fix: force color change when header background changes */
.page.--overlapping-header-dark header:not(.is-scrolled) .header-inner:not(:has(.header-mobile .logo.is-open)) .right .toggle__label {
  color: var(--color-white);
  will-change: color;
  transform: translateZ(0);
  transition: color 0.3s ease;
  -webkit-font-smoothing: antialiased;
  backface-visibility: hidden;
  /* Force layer creation on iOS */
  -webkit-transform: translateZ(0);
  opacity: 1;
}

.page.--overlapping-header-dark header:not(.is-scrolled) .header-inner:not(:has(.header-mobile .logo.is-open)) .right .toggle path,
.page.--overlapping-header-dark header:not(.is-scrolled) .header-inner:not(:has(.header-mobile .logo.is-open)) .right .hamburger path {
  fill: var(--color-white);
  will-change: fill;
  transform: translateZ(0);
  transition: fill 0.3s ease;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  /* Force layer creation on iOS */
  -webkit-transform: translateZ(0);
  opacity: 1;
}

/* Force repaint on iOS when mobile menu state changes */
.page.--overlapping-header-dark header .right .toggle,
.page.--overlapping-header-dark header .right .hamburger {
  /* Create a new stacking context to force repaint */
  position: relative;
  z-index: 1;
}

.page.--overlapping-header-dark header .right .toggle path,
.page.--overlapping-header-dark header .right .hamburger path {
  /* Ensure paths are on their own layer */
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  will-change: fill;
  transition: fill 0.3s ease;
}

</style>
